import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sparkles,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  Loader2,
  Flame,
  Target,
  Heart,
  ArrowLeft,
} from "lucide-react";
import {
  GeneratedQuiz,
  QuizDifficulty,
  QuizModalProps,
  QuizQuestion,
  useDocumentsData,
} from "../Documents/documentsData";
import { generateAiQuestionaires } from "../../dispatchers/documentDispatchers";
import { v4 as uuidv4 } from "uuid";
import { QuizDifficultyType } from "../../enum/QuizDifficultyType";

export default function QuizModal({
  open,
  onOpenChange,
  onSave,
  initialQuiz,
  docTitle,
  docType,
  createdAt,
  sizeKB,
  initialText = "",
  docId,
}: QuizModalProps) {
  const [quiz, setQuiz] = useState<GeneratedQuiz | null>(initialQuiz ?? null);
  const [generating, setGenerating] = useState(false);
  const [showDifficultySelection, setShowDifficultySelection] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<QuizDifficulty>("normal");

  const { documentQuestions } = useDocumentsData();
  const isNew = !initialQuiz && (!quiz || quiz.questions.length === 0);
  const formatUK = (d?: string) =>
    d
      ? new Date(d).toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";
  const wordCount = (initialText || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  useEffect(() => {
    if (open) {
      setQuiz(initialQuiz ?? null);
      setShowDifficultySelection(false);
      setSelectedDifficulty("normal");
    }
  }, [open, initialQuiz]);

  const canSave = useMemo(() => {
    if (!quiz) return false;
    return (
      quiz.questions.length > 0 &&
      quiz.questions.every(
        (q) => q.question.trim().length > 0 && q.options.length > 0
      )
    );
  }, [quiz]);

  const addQuestion = () => {
    setQuiz((prev) => {
      const nextNum = (prev?.questions.length || 0) + 1;
      const q: QuizQuestion = {
        id: uuidv4(),
        question: "",
        options: ["", "", "", ""],
        correctIndex: 0,
      };
      return prev ? { questions: [...prev.questions, q] } : { questions: [q] };
    });
  };

  const deleteQuestion = (index: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      qs.splice(index, 1);
      return { questions: qs };
    });
  };

  const moveQuestion = (index: number, delta: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      const ni = index + delta;
      if (ni < 0 || ni >= qs.length) return prev;
      const [m] = qs.splice(index, 1);
      qs.splice(ni, 0, m);
      return { questions: qs };
    });
  };

  const updateQuestionText = (index: number, text: string) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      qs[index] = { ...qs[index], question: text };
      return { questions: qs };
    });
  };

  const updateOptionText = (qi: number, oi: number, text: string) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      const q = qs[qi];
      const opts = [...q.options];
      opts[oi] = text;
      qs[qi] = { ...q, options: opts };
      return { questions: qs };
    });
  };

  const setCorrectOption = (qi: number, oi: number) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const qs = [...prev.questions];
      qs[qi] = { ...qs[qi], correctIndex: oi };
      return { questions: qs };
    });
  };

  useEffect(() => {
    if (Array.isArray(documentQuestions)) {
      const questions: QuizQuestion[] = documentQuestions?.map((q) => ({
        id: q.questionId || uuidv4(),
        question: q.question,
        options: q.options || [],
        correctIndex: typeof q.correctIndex === "number" ? q.correctIndex : 0,
      }));

      setQuiz({ questions });
    }
  }, [documentQuestions]);

  const generateFromText = async (difficulty: QuizDifficulty) => {
    setGenerating(true);
    setShowDifficultySelection(false);
    const MIN_GENERATE_MS = 1500;
    const started = Date.now();

    try {
      await generateAiQuestionaires({
        _id: docId,
        QuizDifficulty: difficulty,
      });
    } catch (error) {
      console.error("Error generating questions:", error);
      setQuiz({ questions: [] });
    } finally {
      const elapsed = Date.now() - started;
      if (elapsed < MIN_GENERATE_MS) {
        await new Promise((res) => setTimeout(res, MIN_GENERATE_MS - elapsed));
      }
      setGenerating(false);
    }
  };

  // Main render logic
  const renderContent = () => {
    // Show difficulty selection if it's active
    if (showDifficultySelection) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDifficultySelection(false)}
              className="p-1"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="text-base font-semibold">
              Choose Quiz Difficulty
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button
              variant="outline"
              onClick={() => generateFromText(QuizDifficultyType.Easy)}
              disabled={generating}
              className={`h-32 border-2 flex flex-col items-center justify-center gap-2 ${
                selectedDifficulty === "easy"
                  ? "border-green-500 bg-green-50"
                  : "border-gray-300 hover:border-green-400 hover:bg-green-50"
              }`}
            >
              <Heart className="w-8 h-8 text-green-600" />
              <div className="text-base font-semibold text-green-700">
                Easy
              </div>
              <div className="text-xs text-green-600 text-center px-2">
                Simple questions, mostly True/False
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => generateFromText(QuizDifficultyType.Normal)}
              disabled={generating}
              className={`h-32 border-2 flex flex-col items-center justify-center gap-2 ${
                selectedDifficulty === "normal"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
              }`}
            >
              <Target className="w-8 h-8 text-blue-600" />
              <div className="text-base font-semibold text-blue-700">
                Normal
              </div>
              <div className="text-xs text-blue-600 text-center px-2">
                Balanced mix of question types
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => generateFromText(QuizDifficultyType.Hard)}
              disabled={generating}
              className={`h-32 border-2 flex flex-col items-center justify-center gap-2 ${
                selectedDifficulty === "hard"
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300 hover:border-red-400 hover:bg-red-50"
              }`}
            >
              <Flame className="w-8 h-8 text-red-600" />
              <div className="text-base font-semibold text-red-700">
                Hard
              </div>
              <div className="text-xs text-red-600 text-center px-2">
                Complex questions, advanced concepts
              </div>
            </Button>
          </div>
        </div>
      );
    }

    // Show initial selection for new quizzes
    if (isNew) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => setShowDifficultySelection(true)}
            disabled={generating}
            className="h-36 bg-purple-600 hover:bg-purple-700 text-white border-0 flex flex-col items-center justify-center gap-2"
          >
            {generating ? (
              <span className="inline-flex items-center gap-2">
                Generating...
              </span>
            ) : (
              <>
                <Sparkles className="w-10 h-10 text-white" />
                <div className="text-base font-semibold text-white">
                  Generate with AI
                </div>
                <div className="text-xs text-purple-100">
                  Create questions from the document
                </div>
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={addQuestion}
            className="h-36 border-2 border-gray-300 hover:border-gray-500 hover:bg-gray-50 flex flex-col items-center justify-center gap-2"
          >
            <Plus className="w-10 h-10 text-gray-700" />
            <div className="text-base font-semibold">
              Start from scratch
            </div>
            <div className="text-xs text-gray-600">
              Add your own questions
            </div>
          </Button>
        </div>
      );
    }

    // Show quiz editor for existing quizzes
    return (
      <>
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">{docTitle}</div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDifficultySelection(true)}
              disabled={generating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {generating ? (
                "Generating..."
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Generate
                </span>
              )}
            </Button>
            <Button variant="outline" onClick={addQuestion}>
              <Plus className="w-4 h-4" /> Add Question
            </Button>
          </div>
        </div>

        {/* Show questions list OR "No questions yet" message */}
        {quiz && quiz?.questions?.length > 0 ? (
          <div className="max-h-96 overflow-y-auto pr-1">
            <Accordion type="multiple" className="w-full">
              {quiz?.questions?.map((q, qi) => (
                <AccordionItem
                  key={q?.id}
                  value={q?.id}
                  className="border rounded-md mb-2"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full mr-3">
                      <div className="flex items-center gap-3">
                        <div className="text-sm font-medium text-gray-800">
                          Question {qi + 1}
                        </div>
                        <div className="text-xs text-gray-600 truncate max-w-md">
                          {q?.question.trim() || "Untitled question"}
                        </div>
                      </div>
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveQuestion(qi, -1);
                          }}
                          disabled={qi === 0}
                          className="h-8 w-8 p-0"
                        >
                          <span>
                            <ArrowUp className="w-4 h-4" />
                          </span>
                        </Button>
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveQuestion(qi, 1);
                          }}
                          disabled={qi === quiz?.questions?.length - 1}
                          className="h-8 w-8 p-0"
                        >
                          <span>
                            <ArrowDown className="w-4 h-4" />
                          </span>
                        </Button>
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteQuestion(qi);
                          }}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <span>
                            <Trash2 className="w-4 h-4" />
                          </span>
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-3">
                      <div>
                        <Label>Question Text</Label>
                        <Input
                          value={q.question}
                          onChange={(e) =>
                            updateQuestionText(qi, e.target.value)
                          }
                          className="mt-1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Answers</Label>
                        <div className="space-y-2">
                          {q?.options?.map((opt, oi) => (
                            <label
                              key={oi}
                              className="flex items-center gap-3"
                            >
                              <input
                                type="radio"
                                name={`correct-${q?.id}`}
                                checked={q.correctIndex === oi}
                                onChange={() => setCorrectOption(qi, oi)}
                                className="h-4 w-4"
                              />
                              <Input
                                value={opt}
                                onChange={(e) =>
                                  updateOptionText(qi, oi, e.target.value)
                                }
                                className="flex-1"
                              />
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {q?.correctIndex === oi ? "Correct" : ""}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ) : (
          !generating && <div className="text-sm text-gray-600">
            No questions yet. Click Generate or Add Question to start.
          </div>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />{" "}
            {initialQuiz ? "Edit Quiz" : "Add Quiz"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {generating && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Generating with AI…
              This may take a few seconds.
            </div>
          )}
          
          {/* Document info header */}
          <div className="mb-3">
            <div className="text-base font-semibold text-gray-900">
              {docTitle}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {docType ? <span>Type: {docType}</span> : null}
              {docType ? <span className="mx-1">•</span> : null}
              {createdAt ? <span>Created: {formatUK(createdAt)}</span> : null}
              {createdAt ? <span className="mx-1">•</span> : null}
              {typeof sizeKB === "number" ? (
                <span>Size: {sizeKB} KB</span>
              ) : null}
              {typeof sizeKB === "number" ? (
                <span className="mx-1">•</span>
              ) : null}
              <span>Words: {wordCount}</span>
            </div>
          </div>

          {renderContent()}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={() => quiz && onSave(quiz)} disabled={!canSave}>
              Save Quiz
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
